<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import type { OrganizationMember } from "@/model/app/organization-member"
import type { ChampionshipRegistrationMember } from "@/model/championship/championship-registration-member"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import { useChampionshipRegistrationSelectTeamData } from "@/pages/Tasks/Tasks/championship-registration"
import { useRegistrationPolicy } from "@/pages/Tasks/Tasks/useRegistrationPolicy"
import { defineTranslations } from "@/utils/defineTranslations"
import { $date, useHttpClient } from "@/vf"
import { isAxiosError } from "axios"
import { groupBy } from "lodash"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useTaskViewMode } from "../../Issue/util"

const props = defineProps<{
    registrationId: string
    team: ChampionshipRegistrationTeam
}>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const http = useHttpClient()
const { t } = useI18n()
const viewMode = useTaskViewMode()
const data = useChampionshipRegistrationSelectTeamData()
const policy = useRegistrationPolicy(
    computed(() => data.value?.permissions),
    computed(() => viewMode),
)

/*─────────────────────────────────────┐
│  roles                              │
└─────────────────────────────────────*/
type Role = NonNullable<ChampionshipRegistrationMember["type"]>

const roles: Role[] = ["participant", "alternate", "coach", "supervisor"]

const roleLabels: Record<Role, string> = {
    participant: t("@tasks:tasks.championship_registration.team_list.participants"),
    alternate: t("@tasks:tasks.championship_registration.team_list.alternates"),
    coach: t("@tasks:tasks.championship_registration.team_list.coaches"),
    supervisor: t("@tasks:tasks.championship_registration.team_list.supervisors"),
}

/*─────────────────────────────────────┐
│  persons                            │
└─────────────────────────────────────*/
/** One row per person, with the id of the team membership they hold in each role column. */
type PersonRow = {
    person: OrganizationMember | undefined
    name: string
    memberships: Partial<Record<Role, string>>
}

const persons = computed<PersonRow[]>(() =>
    Object.entries(groupBy(props.team.members ?? [], "memberId"))
        .map(([memberId, memberships]) => {
            const person = data.value?.availableMembers.find(m => m.id === memberId)

            return {
                person,
                name: person ? `${person.firstName} ${person.lastName}` : trans("unknown_member"),
                memberships: Object.fromEntries(memberships.map(m => [m.type, m.id])),
            }
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
)

/*─────────────────────────────────────┐
│  blank slots                        │
└─────────────────────────────────────*/
/**
 * Blank slots are booked slots without a named person, one row each with the checkbox in their role column.
 * Entourage slots are shared by coaches and supervisors and listed under coaches. Key: "blank/<role>/<n>".
 */
const blankSlotCounts = computed<Record<Role, number>>(() => ({
    participant: (props.team.numberOfParticipants ?? 0) - (props.team.numberOfNamedParticipants ?? 0),
    alternate: (props.team.numberOfAlternates ?? 0) - (props.team.numberOfNamedAlternates ?? 0),
    coach: (props.team.numberOfEntourages ?? 0) - (props.team.numberOfNamedEntourages ?? 0),
    supervisor: 0,
}))

const blankSlots = computed(() =>
    roles.flatMap(role =>
        Array.from({ length: Math.max(0, blankSlotCounts.value[role]) }, (_, n) => ({
            role,
            key: `blank/${role}/${n}`,
        })),
    ),
)

/*─────────────────────────────────────┐
│  submit                             │
└─────────────────────────────────────*/
const selected = ref<string[]>([])
const errors = ref<string[]>([])

function countSelectedBlankSlots(role: Role): number {
    return selected.value.filter(key => key.startsWith(`blank/${role}/`)).length
}

const cancelConfirm = createInlineConfirm({
    async action() {
        errors.value = []

        try {
            await http.post(
                `/championship/registration/${props.registrationId}/team/${props.team.id}/cancel-members?viewMode=${viewMode}`,
                {
                    members: selected.value.filter(key => !key.startsWith("blank/")),
                    blankParticipants: countSelectedBlankSlots("participant"),
                    blankAlternates: countSelectedBlankSlots("alternate"),
                    blankEntourages: countSelectedBlankSlots("coach"),
                },
            )
        } catch (e) {
            if (!isAxiosError<{ blockers?: string[] } | Record<string, string[]>>(e) || !e.response) {
                throw e
            }

            const { status, data: body } = e.response

            if (status === 400 && "blockers" in body && body.blockers) {
                // The remaining team would break a rule of its category
                errors.value = body.blockers.map(blocker => t(`@championship:registration.blocker.${blocker}`))
            } else if (status === 422) {
                // The selection no longer fits the team, e.g., someone else changed it in the meantime
                errors.value = Object.values(body)
                    .flat()
                    .map(message => t(message))
            }

            throw e
        }
    },
    afterAction() {
        emit("updated")
    },
})

/*─────────────────────────────────────┐
│  translations                       │
└─────────────────────────────────────*/
const trans = defineTranslations({
    hint_direct: {
        de: "Die ausgewählten Personen und freien Plätze werden sofort aus dem Team entfernt und aus der Buchung genommen.",
        en: "The selected persons and blank slots are removed from the team and the booking right away.",
    },
    hint_after_deadline: {
        de: "Der Meldeschluss ist vorbei: Die Stornierung wird als Änderungsantrag eingereicht und automatisch angenommen. Dafür fallen die Stornogebühren der Meisterschaft an.",
        en: "The registration deadline has passed: the cancellation is submitted as a change request and accepted automatically. The championship's cancellation fees apply.",
    },
    blank_slot: {
        de: "Freier Platz",
        en: "Blank slot",
    },
    cancelled: {
        de: "storniert",
        en: "cancelled",
    },
    unknown_member: {
        de: "Unbekannte Person",
        en: "Unknown person",
    },
    confirmation: {
        de: "Möchtest du die ausgewählten Personen und Plätze ({n}) wirklich stornieren?",
        en: "Do you really want to cancel the selected persons and slots ({n})?",
    },
    success: {
        de: "Die Stornierung wurde übernommen.",
        en: "The cancellation has been applied.",
    },
    button: {
        de: "Auswahl stornieren",
        en: "Cancel selection",
    },
})
</script>

<template>
    <div class="mb-4">
        <div class="alert alert-info my-3">
            {{ data?.isRegistrationClosed ? trans("hint_after_deadline") : trans("hint_direct") }}
        </div>

        <div v-if="errors.length" class="alert alert-danger">
            <p v-for="(error, index) in errors" :key="index" class="mb-0">{{ error }}</p>
        </div>

        <div class="danger">
            <table class="table">
                <thead>
                    <tr>
                        <th class="align-bottom">{{ $t("@app:organization_member.name") }}</th>
                        <th class="align-bottom">{{ $t("@app:organization_member.birthDate") }}</th>
                        <th class="align-bottom">{{ $t("@app:organization_member.gender") }}</th>
                        <th v-for="role in roles" :key="role">{{ roleLabels[role] }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in persons" :key="row.person?.id ?? row.name">
                        <td class="text-nowrap">
                            <i class="fa fa-fw fa-mars text-muted" v-if="row.person?.genderForChampionship === 'm'"></i>
                            <i
                                class="fa fa-fw fa-venus text-muted"
                                v-if="row.person?.genderForChampionship === 'f'"
                            ></i>
                            {{ row.name }}
                        </td>
                        <td>{{ row.person ? $date(row.person.birthDate) : "" }}</td>
                        <td>{{ row.person ? $t("crud:gender." + row.person.gender) : "" }}</td>
                        <td v-for="role in roles" :key="role" class="text-danger text-nowrap">
                            <template v-if="row.memberships[role]">
                                <input v-model="selected" type="checkbox" class="custom-checkbox" :value="row.memberships[role]" />
                                <!-- invisible instead of hidden, so that ticking a box does not shift the layout -->
                                <span class="ml-2" :class="{ invisible: !selected.includes(row.memberships[role]!) }">
                                    {{ trans("cancelled") }}
                                </span>
                            </template>
                        </td>
                    </tr>
                    <tr v-for="slot in blankSlots" :key="slot.key">
                        <td class="text-muted" colspan="3">{{ trans("blank_slot") }}</td>
                        <td v-for="role in roles" :key="role" class="text-danger text-nowrap">
                            <template v-if="slot.role === role">
                                <input v-model="selected" type="checkbox" class="custom-checkbox" :value="slot.key" />
                                <span class="ml-2" :class="{ invisible: !selected.includes(slot.key) }">
                                    {{ trans("cancelled") }}
                                </span>
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <VarsityInlineConfirmButton
            :controller="cancelConfirm"
            class="btn btn-danger btn-shape-skewed"
            :disabled="!policy.CancelTeamMembers || !selected.length"
        >
            {{ trans("button") }}
        </VarsityInlineConfirmButton>

        <VarsityInlineConfirm :controller="cancelConfirm">
            <template #confirmation>
                {{ trans("confirmation", { n: selected.length }) }}
            </template>
            <template #success>
                {{ trans("success") }}
            </template>
        </VarsityInlineConfirm>
    </div>
</template>

<style scoped>
.custom-checkbox:checked {
    background-color: #fd6e6e !important;
}
</style>
